import { Hidden } from "@material-ui/core";
import { FC, useCallback, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { boolean } from "yup";
import { Toggle } from "../../../../../components/forms/toggle";
import { Field } from "../../internal/field";
import {
  ConfirmText,
  ErrorText,
  FieldHook,
  FullWidthItem,
  RenderFuncProps,
  ToggleWrapper,
} from "../common";

const Elem: FC<RenderFuncProps> = ({ item }) => {
  const { t } = useTranslation("fields");
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: item.field,
  });

  const innerDescription = useMemo(
    () => (
      <div>
        <div>
          <strong>{t("sekuritance-statements")}</strong>
        </div>
        {error?.message ? <ErrorText>{error.message}</ErrorText> : null}
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              "i-confirm-that-i-have-read-and-agree-to-the-sekuritance"
            ),
          }}
        ></div>
      </div>
    ),
    [t, error]
  );

  const innerToggle = useMemo(
    () => (
      <ToggleWrapper>
        <Toggle {...field} />
        <ConfirmText ticked={field.value}>{t("i-confirm")}</ConfirmText>{" "}
      </ToggleWrapper>
    ),
    [field, t]
  );

  return (
    <>
      <Hidden smDown>
        {innerToggle}
        {innerDescription}
      </Hidden>
      <Hidden mdUp>
        <FullWidthItem>
          {innerToggle}
          {innerDescription}
        </FullWidthItem>
      </Hidden>
    </>
  );
};

const useSchema = () => {
  const { t } = useTranslation("fields");
  const ERROR_TEXT = useMemo(() => t("you-must-agree-to-the-statements"), [t]);

  return useCallback(
    (f: Field.DefaultField) =>
      (f.optional
        ? boolean()
        : boolean().required(ERROR_TEXT).isTrue(ERROR_TEXT)
      ).label(t("sekuritance-statements")),
    [t, ERROR_TEXT]
  );
};

export const useSekuritanceStatementsField: FieldHook = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      skipOptionalCheck: true,
      schema,
      elem: Elem,
    }),
    [schema]
  );
};
